import { useEffect, useState } from 'react';

import { Box, Checkbox, Divider, FormControlLabel, FormGroup, Grid, List, ListItem, Stack, TextField, Typography } from '@mui/material';

import { isValid } from 'date-fns';
import { toDate, formatDate } from '../../../../utils/dateHelpers';

import GuardianOrCaregiverInput from '../../Shared/GuardianOrCaregiverInput';
import Initials from '../../Shared/Initials';
import Signature from '../../Shared/Signature';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../../../store/store';
import { RootState } from '../../../../reducers';
import { ConsentSignatureTypeEnum } from '../../../../Enum/ConsentSignatureTypeEnum';
import { setDigitalSignatureForm } from '../../../../store/consentSlice.slice';
import SignatureTypeTabs from '../../Shared/SignatureTypeTabs';

const ConsentPartOneSpanish: React.FC = () => {
    const { activeStep, digitalSignatureForm } = useSelector((state: RootState) => state.consentSlice);

    const dispatch = useAppDispatch();

    const updateDigitalSignatureForm = (key: string, value: any) => {
        dispatch(
            setDigitalSignatureForm({
                ...digitalSignatureForm,
                [key]: value,
            })
        );
    };

    const [openGuardianOrCareGiverOne, setOpenGuardianOrCareGiverOne] = useState(false);

    const [openGuardianOrCareGiverWitnessOne, setOpenGuardianOrCareGiverWitnessOne] = useState(false);

    const [openSectionOne, setOpenSectionOne] = useState(false);
    const [openSectionTwo, setOpenSectionTwo] = useState(false);
    const [openSectionThree, setOpenSectionThree] = useState(false);
    const [openSectionFour, setOpenSectionFour] = useState(false);
    const [openSectionFive, setOpenSectionFive] = useState(false);

    const [openMemberSignatureOne, setOpenMemberSignatureOne] = useState(false);

    const [openVerbalConsent, setOpenVerbalConsent] = useState(false);

    const handleConsentChange = (consentType: string) => {
        dispatch(
            setDigitalSignatureForm({
                ...digitalSignatureForm,
                AppointmentConsent: consentType,
            })
        );
    };

    const handleChangeSignatureType = (event: React.SyntheticEvent, newValue: ConsentSignatureTypeEnum) => {
        dispatch(
            setDigitalSignatureForm({
                ...digitalSignatureForm,
                SignatureType: newValue,
            })
        );
    };

    useEffect(() => {
        const element = document.getElementById('top');
        element.scrollIntoView({ behavior: 'smooth' });
    }, [activeStep]);

    return (
        <Box sx={{ position: 'relative' }}>
            <div id="top" className="initials-div"></div>
            <Typography variant="h5" p={2} fontWeight={'bold'} mt={5}>
                CONSENTIMIENTO AL TRATAMIENTO DADO POR CHP CARE PC Y ACUSE DE RECIBO DEL AVISO SOBRE PRÁCTICAS DE PRIVACIDAD
            </Typography>
            <Stack spacing={2} sx={{ padding: 2, textAlign: 'left' }}>
                <section id="consent-for-services">
                    <Typography variant="h6" mb={1} textAlign={'center'}>
                        CONSENTIMIENTO A CHP CARE PC PARA EL USO DE SERVICIOS
                    </Typography>
                    <Typography variant="body1">
                        Solicito y autorizo todo cuidado médico que mi proveedor, sus asistentes, o quienes sean designados (referidos colectivamente como
                        “proveedores”), declaren necesario o recomendable. Este cuidado puede incluir, pero no está limitado a: diagnósticos rutinarios;
                        procedimientos de radiología y de laboratorio; administración rutinaria de medicinas, terapias biológicas y de otra índole; y,
                        valoraciones y tratamientos médicos, de comportamiento, y del cuidado de la salud, rutinarios. Autorizo a mi(s) proveedor(es) a realizar
                        cualquier servicio adicional o extendido en situaciones de emergencia si es necesario o recomendable con el fin de conservar mi salud o
                        vida. Entiendo que mi (el miembro) cuidado es dirigido por mi(s) proveedor(es) a la vez que personal adicional es quien me otorga
                        cuidado y servicios a mí (el miembro) siguiendo las instrucciones de mi(s) proveedor(es).
                    </Typography>
                    <List sx={{ listStyleType: 'disc', p: 2 }}>
                        <ListItem sx={{ display: 'list-item' }}>
                            <Typography variant="body1">
                                Estoy consciente de que la praxis de la medicina no es una ciencia exacta, y reconozco que no se me han dado garantías ni hecho
                                promesas respecto a los resultados del dicho tratamiento.
                            </Typography>
                        </ListItem>
                        <ListItem sx={{ display: 'list-item' }}>
                            <Typography variant="body1">
                                He recibido toda la información que necesito para dar consentimiento informado para los tratamientos que se me han propuesto.
                                Esta información incluye los riesgos y beneficios de dichos tratamientos.
                            </Typography>
                        </ListItem>
                        <ListItem sx={{ display: 'list-item' }}>
                            <Typography variant="body1">
                                Comprendo que CHP Care PC provee servicios médicos y servicios relacionados a salud del comportamiento como un equipo
                                profesional de tratamiento incluyendo terapia individual y familiar.
                            </Typography>
                        </ListItem>
                    </List>
                    <Typography variant="body1">
                        Su participación es voluntaria con el derecho a participar en el desarrollo de y revisión de planes de tratamiento. Usted tiene el
                        derecho de rehusar cualquier tratamiento recomendado, o el de rescindir consentimiento para el tratamiento y a que se le expliquen las
                        consecuencias de rehusarse. Usted tiene el derecho a sus archivos médicos y se le ha notificado de las limitaciones de confidencialidad
                        ya que información confidencial podría ser divulgada sin mi consentimiento en acuerdo con la ley estatal y federal. Usted puede
                        notificar su equipo clínico o llamar a CHP Care al 602-802-8388 para una copia de su archivo en cualquier momento.
                    </Typography>
                    <div className="initials-div" id="section-one"></div>
                    <Box my={2} width={'50vw'}>
                        <Initials
                            isSpanish={true}
                            isOpen={openSectionOne}
                            setIsOpen={setOpenSectionOne}
                            initials={digitalSignatureForm.SectionOneInitials}
                            setInitials={(initials: string) =>
                                dispatch(
                                    setDigitalSignatureForm({
                                        ...digitalSignatureForm,
                                        SectionOneInitials: initials,
                                    })
                                )
                            }
                        />
                    </Box>
                </section>
                <section id="privacy-practices">
                    <Typography variant="h6" mb={1} textAlign={'center'}>
                        ACEPTACIÓN DE LAS PRÁCTICAS DE PRIVACIDAD
                    </Typography>
                    <Typography variant="body1">
                        El Aviso de Prácticas de Privacidad de Care CHP PC provee información sobre como la información de salud protegida sobre mí (paciente) –
                        incluyendo información sobre el virus de inmunodeficiencia humana (VIH), complejo relacionado al SIDA (ARC por sus siglas en inglés) y
                        síndrome de inmunodeficiencia adquirida (SIDA); incluyendo registros sobre tratamientos para abuso de substancias protegidos bajo la
                        regulación 42 parte 2 en el Código Federal de Regulaciones (de existir); y registros psicológicos y sobre servicios sociales incluyendo
                        comunicaciones que se me han hecho por parte de trabajadores sociales o psicólogos (de existir). Se me ha provisto la oportunidad para
                        revisar el Aviso antes de firmar este documento de consentimiento. Adicionalmente, CHP Care PC participa en el intercambio de
                        información de salud de Arizona, denominado Health Current, para compartir mi información sobre cuidado de la salud de manera
                        electrónica y así facilitar la provisión de cuidados con otros proveedores que estén involucrados en mis cuidados. CHP Care PC me ha
                        provisto una copia del Aviso sobre Prácticas de Información de Salud, que incluye mi derecho de “No Participar” para que mi información
                        no sea compartida con Health Current. Entiendo que los términos y condiciones de dicho Aviso pueden cambiar, y que puedo obtener una
                        copia de la versión actualizada contactando la oficina de CHP Care PC.
                    </Typography>
                    <List sx={{ listStyleType: 'disc', p: 2 }}>
                        <ListItem sx={{ display: 'list-item' }}>
                            <Typography variant="body1">
                                Entiendo que tengo el derecho de solicitar la aplicación de restricciones sobre como mi información de salud protegida se usa o
                                divulga para fines de tratamiento, pago u operaciones de cuidado de la salud. CHP Care PC no está obligado a aceptar estas
                                restricciones (con la excepción de “No Participar” con Health Current), pero, de aceptar, estarán sujetos a dicho acuerdo.
                            </Typography>
                        </ListItem>
                        <ListItem sx={{ display: 'list-item' }}>
                            <Typography variant="body1">
                                Al firmar este formulario, acepto que se me ha ofrecido y/o he recibido el Aviso de Prácticas de Privacidad de CHP Care PC,
                                incluyendo el Aviso sobre Prácticas de Información de Salud.
                            </Typography>
                        </ListItem>
                    </List>
                    <div className="initials-div" id="section-two"></div>
                    <Box my={2} width={'50vw'}>
                        <Initials
                            isSpanish={true}
                            isOpen={openSectionTwo}
                            setIsOpen={setOpenSectionTwo}
                            initials={digitalSignatureForm.SectionTwoInitials}
                            setInitials={(initials: string) =>
                                dispatch(
                                    setDigitalSignatureForm({
                                        ...digitalSignatureForm,
                                        SectionTwoInitials: initials,
                                    })
                                )
                            }
                        />
                    </Box>
                    <Typography variant="body1">
                        Entiendo que, como parte de mis cuidados de salud, CHP Care PC crea y mantiene registros en papel y/o electrónicos que describen mi
                        historial de salud, síntomas, examinaciones y resultados de pruebas, diagnósticos, tratamientos y cualesquiera planes para cuidado o
                        tratamientos a futuro. Entiendo que mi historial de medicación y formularios de beneficios se pueden descargar de un centro de
                        información electrónica asegurado. Entiendo que esta información sirve de:
                    </Typography>
                    <List sx={{ listStyleType: 'disc', p: 2 }}>
                        <ListItem sx={{ display: 'list-item' }}>
                            <Typography variant="body1">Como una base para la planeación de mis cuidados y tratamientos</Typography>
                        </ListItem>
                        <ListItem sx={{ display: 'list-item' }}>
                            <Typography variant="body1">
                                Como un medio de comunicación entre muchos profesionales de la salud que contribuyen a mi cuidado
                            </Typography>
                        </ListItem>
                        <ListItem sx={{ display: 'list-item' }}>
                            <Typography variant="body1">
                                Como una fuente de información para aplicar mi información quirúrgica y diagnóstico a mi historial médico
                            </Typography>
                        </ListItem>
                        <ListItem sx={{ display: 'list-item' }}>
                            <Typography variant="body1">
                                Un medio a través del cual un tercero que realice pagos puede verificar que servicios fueron provistos
                            </Typography>
                        </ListItem>
                        <ListItem sx={{ display: 'list-item' }}>
                            <Typography variant="body1">
                                Como una herramienta para operaciones rutinarias del cuidado de la salud, como valorizar calidad y evaluar el desempeño de
                                profesionales del cuidado de la salud
                            </Typography>
                        </ListItem>
                        <ListItem sx={{ display: 'list-item' }}>
                            <Typography variant="body1">
                                En caso de optar por recibir confirmaciones para citas a través de mensajes de texto y/o correo electrónico (e-mail), fuente de
                                información que permite a mis proveedores crear y mandar dichas confirmaciones para citas mediante mensajes y/o e-mail.
                            </Typography>
                        </ListItem>
                    </List>
                    <div className="initials-div" id="section-three"></div>
                    <Box my={2} width={'50vw'}>
                        <Initials
                            isSpanish={true}
                            isOpen={openSectionThree}
                            setIsOpen={setOpenSectionThree}
                            initials={digitalSignatureForm.SectionThreeInitials}
                            setInitials={(initials: string) =>
                                dispatch(
                                    setDigitalSignatureForm({
                                        ...digitalSignatureForm,
                                        SectionThreeInitials: initials,
                                    })
                                )
                            }
                        />
                    </Box>
                    <Typography variant="body1" fontWeight={'bold'}>
                        Acepto que una copia del Aviso de Prácticas de Privacidad me ha sido provista. Entiendo que tengo los siguientes derechos y privilegios:
                    </Typography>
                    <List sx={{ listStyleType: 'disc', p: 2 }}>
                        <ListItem sx={{ display: 'list-item' }}>
                            <Typography variant="body1">El derecho a revisar el aviso antes de firmar este consentimiento</Typography>
                        </ListItem>
                        <ListItem sx={{ display: 'list-item' }}>
                            <Typography variant="body1">El derecho a objetar al uso de mi información médica para propósitos de directorio</Typography>
                        </ListItem>
                        <ListItem sx={{ display: 'list-item' }}>
                            <Typography variant="body1">
                                El derecho a solicitar restricciones sobre como puede usarse o divulgarse mi información de salud para operaciones del cuidado
                                de la salud o tratamientos
                            </Typography>
                        </ListItem>
                    </List>
                    <Typography variant="body1">
                        Entiendo que CHP Care PC no está obligado a aceptar las restricciones solicitadas. Entiendo que puedo revocar este consentimiento por
                        escrito, exceptuando para fines los cuales la organización ya haya hecho uso de este. Además, entiendo que al rehusarme a firmar o
                        revocar este consentimiento, la organización puede a su vez rehusarse a tratarme, como lo permite la Sección 164.506 del Código Federal
                        de Regulaciones.
                    </Typography>
                    <Stack spacing={2} my={2}>
                        <Typography variant="body1">Deseo aplicar la siguiente restricción sobre el uso y divulgación de mi información de salud:</Typography>
                        <TextField
                            fullWidth
                            label="Restricciones de información"
                            multiline
                            variant="outlined"
                            value={digitalSignatureForm.InfoRestrictions}
                            onChange={(e) => updateDigitalSignatureForm('InfoRestrictions', e.target.value)}
                        />
                    </Stack>
                    <Typography variant="body1">
                        Adicionalmente, entiendo que CHP Care PC conserva el derecho de cambiar su aviso y prácticas, acorde a lo establecido por la Sección
                        164.520 del Código Federal de Regulaciones. De ser el caso que CHP Care PC cambie su aviso, se enviará una copia de cualquier revisión a
                        la dirección que he provisto (mediante el servicio postal de los Estados Unidos, o, si acepto, e-mail).
                    </Typography>
                    <div className="initials-div" id="section-four"></div>
                    <Box my={2} width={'50vw'}>
                        <Initials
                            isSpanish={true}
                            isOpen={openSectionFour}
                            setIsOpen={setOpenSectionFour}
                            initials={digitalSignatureForm.SectionFourInitials}
                            setInitials={(initials: string) =>
                                dispatch(
                                    setDigitalSignatureForm({
                                        ...digitalSignatureForm,
                                        SectionFourInitials: initials,
                                    })
                                )
                            }
                        />
                    </Box>
                </section>
                <section id="text-email-appointment">
                    <div className="initials-div" id="appointment-consent"></div>
                    <Typography variant="h6" mb={1} mt={2} textAlign={'center'}>
                        CONSENTIMIENTO A CONFIRMACIÓN DE CITAS MEDIANTE MENSAJES DE TEXTO Y/O E-MAIL
                    </Typography>

                    <Typography variant="body1">
                        CHP Care PC ofrece a los pacientes la opción de recibir confirmaciones de citas mediante mensajes de text y/o e-mail. Entiendo que CHP
                        Care PC tendrá acceso a mi información de salud protegida; específicamente, mi información relacionada a cuidados y tratamientos
                        futuros, para poder enviar estas confirmaciones de citas mediante mensajes de texto y/o e-mail.
                    </Typography>

                    <FormGroup>
                        <Stack spacing={2} my={2}>
                            <FormControlLabel
                                control={
                                    <Checkbox checked={digitalSignatureForm.AppointmentConsent === 'consent'} onChange={() => handleConsentChange('consent')} />
                                }
                                label={
                                    <Typography>
                                        Yo <b>doy mi consentimiento </b> a que CHP Care PC acceda a, y use, mi información de salud protegida, para que CHP Care
                                        PC pueda enviarme confirmaciones de cita mediante mensajes de texto y/o e-mail. He completado la Autorización de
                                        Liberación de Información de Salud que viene adjunta a este documento de consentimiento.
                                    </Typography>
                                }
                            />
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={digitalSignatureForm.AppointmentConsent === 'noconsent'}
                                        onChange={() => handleConsentChange('noconsent')}
                                    />
                                }
                                label={
                                    <Typography>
                                        Yo <b>no doy mi consentimiento</b> a que CHP Care PC cceda a, y use, mi información de salud protegida, para que CHP
                                        Care PC pueda enviarme confirmaciones de cita mediante mensajes de texto y/o e-mail.
                                    </Typography>
                                }
                            />
                        </Stack>
                    </FormGroup>
                </section>
                <section>
                    <div className="initials-div" id="section-five"></div>
                    <Box my={2} width={'50vw'}>
                        <Initials
                            isSpanish={true}
                            isOpen={openSectionFive}
                            setIsOpen={setOpenSectionFive}
                            initials={digitalSignatureForm.SectionFiveInitials}
                            setInitials={(initials: string) =>
                                dispatch(
                                    setDigitalSignatureForm({
                                        ...digitalSignatureForm,
                                        SectionFiveInitials: initials,
                                    })
                                )
                            }
                        />
                    </Box>
                    <Typography id="consent-signature" variant="h6" mb={1} my={'25px'}>
                        HE LEÍDO, O ALGUIEN ME HA LEÍDO, ESTE CONSENTIMIENTO Y LO ENTIENDO POR COMPLETO; HE TENIDO LA OPORTUNIDAD DE HACER PREGUNTAS Y MIS
                        PREGUNTAS HAN SIDO RESPONDIDAS
                    </Typography>
                </section>
            </Stack>
            <SignatureTypeTabs isSpanish={true} />
            <Divider />
            <Box px={2} py={4} textAlign={'left'}>
                {/*Member Signature Box */}
                {digitalSignatureForm.SignatureType === ConsentSignatureTypeEnum.Patient && (
                    <>
                        <Grid container spacing={2}>
                            <Grid size={6}>
                                <TextField
                                    id="member-name"
                                    variant="standard"
                                    value={digitalSignatureForm.MemberName}
                                    label={`Nombre del paciente`}
                                    fullWidth
                                    error={digitalSignatureForm.MemberName.length === 0}
                                    onChange={(e) => {
                                        updateDigitalSignatureForm('MemberName', e.target.value);
                                    }}
                                />
                            </Grid>
                            <Grid size={6}>
                                <TextField variant="standard" value={`${formatDate(new Date(), 'MM-dd-yyyy')}`} label={`Fecha`} fullWidth />
                            </Grid>
                        </Grid>
                        <Signature
                            isSpanish={true}
                            buttonLabel={'Firma'}
                            isOpen={openMemberSignatureOne}
                            setIsOpen={setOpenMemberSignatureOne}
                            signature={digitalSignatureForm.MemberSignatureOne}
                            setSignature={(signature: string) =>
                                dispatch(
                                    setDigitalSignatureForm({
                                        ...digitalSignatureForm,
                                        MemberSignatureOne: signature,
                                    })
                                )
                            }
                        />
                    </>
                )}
                {/*Guardian or Caregiver Signature Box */}
                {digitalSignatureForm.SignatureType === ConsentSignatureTypeEnum.GuardianOrCaregiver && (
                    <GuardianOrCaregiverInput
                        isSpanish={true}
                        openGuardianOrCaregiver={openGuardianOrCareGiverOne}
                        setOpenGuardianOrCaregiver={setOpenGuardianOrCareGiverOne}
                        guardianOrCareGiver={digitalSignatureForm.GuardianOrCaregiverSignatureOne}
                        setGuardianOrCareGiver={(signature: string) =>
                            dispatch(
                                setDigitalSignatureForm({
                                    ...digitalSignatureForm,
                                    GuardianOrCaregiverSignatureOne: signature,
                                })
                            )
                        }
                        openGuardianOrCareGiverWitness={openGuardianOrCareGiverWitnessOne}
                        setOpenGuardianOrCareGiverWitness={setOpenGuardianOrCareGiverWitnessOne}
                        guardianOrCareGiverWitness={digitalSignatureForm.WitnessOne}
                        setGuardianOrCareGiverWitness={(signature: string) => updateDigitalSignatureForm('WitnessOne', signature)}
                    />
                )}
                {/*Verbal consent Signature Box */}
                {digitalSignatureForm.SignatureType === ConsentSignatureTypeEnum.Verbal && (
                    <>
                        {/* Verbal Consent */}
                        <Stack>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={digitalSignatureForm.VerbalConsent}
                                        onChange={() =>
                                            dispatch(
                                                setDigitalSignatureForm({
                                                    ...digitalSignatureForm,
                                                    VerbalConsent: !digitalSignatureForm.VerbalConsent,
                                                })
                                            )
                                        }
                                    />
                                }
                                label={
                                    <Typography>
                                        Consentimiento verbal es requerido si el miembro o representante legal es incapaz físicamente de firmar al momento y el
                                        consentimiento de cuidados ha sido explícitamente otorgado. El consentimiento verbal debe ser atestiguado por dos
                                        personas telefónicamente o en persona.
                                    </Typography>
                                }
                            />
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={digitalSignatureForm.ReviewedOne}
                                        onChange={() =>
                                            dispatch(
                                                setDigitalSignatureForm({
                                                    ...digitalSignatureForm,
                                                    ReviewedOne: !digitalSignatureForm.ReviewedOne,
                                                })
                                            )
                                        }
                                    />
                                }
                                label={
                                    <Typography>
                                        <b> El proveedor ha revisado esta página del formulario de consentimiento con el miembro</b>
                                    </Typography>
                                }
                            />
                            <Grid container spacing={2}>
                                <Grid size={6}>
                                    <TextField
                                        variant="outlined"
                                        required
                                        fullWidth
                                        helperText="Firma Digital del Testigo"
                                        sx={{ mt: 5 }}
                                        label={'Testigo Uno'}
                                        value={digitalSignatureForm.VerbalConsentWitnessOne}
                                        onChange={(e) =>
                                            dispatch(
                                                setDigitalSignatureForm({
                                                    ...digitalSignatureForm,
                                                    VerbalConsentWitnessOne: e.target.value,
                                                })
                                            )
                                        }
                                    />
                                </Grid>
                                <Grid size={6}>
                                    <TextField
                                        variant="outlined"
                                        required
                                        fullWidth
                                        helperText="Firma Digital del Testigo"
                                        sx={{ mt: 5 }}
                                        label={'Testigo Dos'}
                                        value={digitalSignatureForm.VerbalConsentWitnessTwo}
                                        onChange={(e) =>
                                            dispatch(
                                                setDigitalSignatureForm({
                                                    ...digitalSignatureForm,
                                                    VerbalConsentWitnessTwo: e.target.value,
                                                })
                                            )
                                        }
                                    />
                                </Grid>
                                <Grid size={12}>
                                    <Signature
                                        isSpanish={true}
                                        buttonLabel={'Firma del Testigo'}
                                        isOpen={openVerbalConsent}
                                        setIsOpen={setOpenVerbalConsent}
                                        signature={digitalSignatureForm.VerbalConsentSignature}
                                        setSignature={(signature: string) =>
                                            dispatch(
                                                setDigitalSignatureForm({
                                                    ...digitalSignatureForm,
                                                    VerbalConsentSignature: signature,
                                                })
                                            )
                                        }
                                    />
                                </Grid>
                            </Grid>
                        </Stack>
                    </>
                )}
            </Box>
        </Box>
    );
};

export default ConsentPartOneSpanish;
